import React from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import Layout from '~/components/Layout/Layout';
import LeagueRiverComponent from '~/components/LeagueRiver/LeagueRiver';

import MetaTags from '~/components/MetaTags/MetaTags';
import NotFound from '~/components/NotFound/NotFound';
import ContentCardTypes from '~/constants/ContentCardTypes';
import logEvent from '~/util/analytics';
import includes from 'lodash/includes';

import { getContentCard } from '~/util/getContent';
import ContentCardComponent from '~/components/ContentCard/ContentCard';

import { initStore } from '~/state/store';
import { operations } from '~/state/pageData';
import withState from '~/hocs/withState';

import css from '~/pages/content_card.scss';

class ContentCard extends React.Component {
  static async getInitialProps(props) {
    const storedContent = props.store.getState().relatedContent.trendingContent;
    const json = await getContentCard(props.query.contentId, storedContent);

    // Override withPageData settings to use content card league info
    if (!props.store.getState().menuData.data) await props.store.dispatch(operations.setMenu());

    props.store.dispatch(operations.setAdConfig(json.content_card));

    const league = props.store.getState().pageData.currentLeague;

    await props.store.dispatch(
      operations.loadLeagueRiver({
        league,
        currentCardId: null,
        before: null,
        riverData: { relatedContent: [], newBefore: null },
      })
    );

    if (
      !json.content_card ||
      !includes(ContentCardTypes.ENABLED_CARD_TYPES, json.content_card.type)
    ) {
      // eslint-disable-next-line
      props.res.statusCode = 404;
    }

    // Anything returned here will end up in props
    return {
      contentCard: json.content_card,
      league,
    };
  }

  // eslint-disable-next-line no-undef
  state: Object;

  componentDidMount() {
    logEvent('page_view', {
      page_type: 'content_card',
      content_id: this.props.contentCard.id,
      content_card_type: this.props.contentCard.type,
    });
  }

  componentDidUpdate(oldProps) {
    if (this.props.url.asPath !== oldProps.url.asPath)
      logEvent('page_view', {
        page_type: 'content_card',
        content_id: this.props.contentCard.id,
        content_card_type: this.props.contentCard.type,
      });
  }

  render() {
    const { contentCard, riverData, league } = this.props;
    if (!contentCard || !includes(ContentCardTypes.ENABLED_CARD_TYPES, contentCard.type)) {
      return <NotFound leagueSlug={league.slug} />;
    }
    return (
      <Layout>
        <Head>
          <style dangerouslySetInnerHTML={{ __html: css.stylesheet }} />
          {contentCard.type !== ContentCardTypes.THE_SCORE_ARTICLE && (
            <meta name="robots" content="noindex" />
          )}
        </Head>
        <MetaTags type="contentCard" content={contentCard} />
        <LeagueRiverComponent league={league} riverData={riverData} currentCardId={contentCard.id}>
          <ContentCardComponent contentCard={contentCard} currentCard isLarge />
        </LeagueRiverComponent>
      </Layout>
    );
  }
}

ContentCard.propTypes = {
  contentCard: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    tags: PropTypes.array,
  }).isRequired,
  league: PropTypes.shape({
    slug: PropTypes.string,
    short_name: PropTypes.string,
    medium_name: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  riverData: PropTypes.shape({
    relatedContent: PropTypes.array,
    before: PropTypes.string,
  }).isRequired,
  experiments: PropTypes.array,
  url: PropTypes.object.isRequired,
};

ContentCard.defaultProps = {
  experiments: [],
};

ContentCard.displayName = 'ContentCardPage';

export default withState({ createStore: initStore })(ContentCard);
